import React, { ReactNode, forwardRef } from 'react';
import styled, { css } from 'styled-components';

export interface InputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  size?: 'small' | 'medium' | 'large';
  variant?: 'outlined' | 'filled';
  fullWidth?: boolean;
  className?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const getIconPosition = (
  leftIcon?: ReactNode,
  rightIcon?: ReactNode,
): 'left' | 'right' | undefined => {
  if (rightIcon) return 'right';
  if (leftIcon) return 'left';
  return undefined;
};

const InputContainer = styled.div<{
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};

  svg {
    position: absolute;
    ${({ iconPosition, theme }) =>
      `${iconPosition === 'right' ? 'right' : 'left'}: ${theme.spacing[3]}`};
    color: ${({ theme }) => theme.colors.text.secondary};
    width: 20px;
    height: 20px;
    pointer-events: none;
  }
`;

const getSizeStyles = (size: InputProps['size']): any => {
  const sizes = {
    small: css`
      height: 36px;
      font-size: 14px;
    `,
    medium: css`
      height: 44px;
      font-size: 16px;
    `,
    large: css`
      height: 52px;
      font-size: 18px;
    `,
  };

  return sizes[size || 'medium'];
};

const getVariantStyles = (
  variant: InputProps['variant'],
  error?: boolean,
): any => {
  const variants = {
    outlined: css`
      background-color: ${({ theme }) => theme.colors.background.paper};
      border: 1px solid
        ${({ theme }) =>
          error
            ? theme.colors.error.main
            : theme.mode === 'dark'
              ? theme.colors.neutral[600]
              : theme.colors.divider};

      &:focus {
        outline: none;
        border-color: ${({ theme }) =>
          error ? theme.colors.error.main : theme.colors.primary.main};
        box-shadow: 0 0 0 3px
          ${({ theme }) =>
            error
              ? `${theme.colors.error.main}20`
              : `${theme.colors.primary.main}20`};
      }

      &:hover:not(:focus):not(:disabled) {
        border-color: ${({ theme }) =>
          error
            ? theme.colors.error.main
            : theme.mode === 'dark'
              ? theme.colors.neutral[500]
              : theme.colors.neutral[400]};
      }
    `,
    filled: css`
      background-color: ${({ theme }) =>
        theme.mode === 'dark'
          ? theme.colors.neutral[800]
          : theme.colors.neutral[100]};
      border: 1px solid transparent;
      border-bottom: 2px solid
        ${({ theme }) =>
          error
            ? theme.colors.error.main
            : theme.mode === 'dark'
              ? theme.colors.neutral[600]
              : theme.colors.neutral[300]};

      &:focus {
        outline: none;
        background-color: ${({ theme }) =>
          theme.mode === 'dark'
            ? theme.colors.neutral[700]
            : theme.colors.background.paper};
        border-bottom-color: ${({ theme }) =>
          error ? theme.colors.error.main : theme.colors.primary.main};
      }

      &:hover:not(:focus):not(:disabled) {
        background-color: ${({ theme }) =>
          theme.mode === 'dark'
            ? theme.colors.neutral[700]
            : theme.colors.neutral[200]};
      }
    `,
  };

  return variants[variant || 'outlined'];
};

const StyledInput = styled.input<
  InputProps & { hasIcon?: boolean; iconPosition?: 'left' | 'right' }
>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  padding-left: ${({ hasIcon, iconPosition, theme }) =>
    hasIcon && iconPosition === 'left' ? theme.spacing[10] : theme.spacing[3]};
  padding-right: ${({ hasIcon, iconPosition, theme }) =>
    hasIcon && iconPosition === 'right' ? theme.spacing[10] : theme.spacing[3]};
  font-family: ${({ theme }) => theme.typography.fontFamily.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text.primary} !important;
  -webkit-text-fill-color: ${({ theme }) =>
    theme.colors.text.primary} !important;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all ${({ theme }) => theme.transitions.duration.base}
    ${({ theme }) => theme.transitions.easing.inOut};

  ${({ size }) => getSizeStyles(size)}
  ${({ variant, error }) => getVariantStyles(variant, error)}

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.hint};
    opacity: 1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${({ theme }) =>
      theme.mode === 'dark'
        ? theme.colors.neutral[800]
        : theme.colors.neutral[100]} !important;
  }

  /* Fix for autofill styles */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: ${({ theme }) =>
      theme.colors.text.primary} !important;
    -webkit-box-shadow: 0 0 0 30px
      ${({ theme }) => theme.colors.background.paper} inset !important;
    box-shadow: 0 0 0 30px ${({ theme }) => theme.colors.background.paper} inset !important;
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const HelperText = styled.span<{ error?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ error, theme }) =>
    error ? theme.colors.error.main : theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
  display: block;
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      placeholder,
      value,
      defaultValue,
      disabled = false,
      required = false,
      error = false,
      helperText,
      leftIcon,
      rightIcon,
      size = 'medium',
      variant = 'outlined',
      fullWidth = false,
      className,
      onChange,
      onFocus,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const iconPosition = getIconPosition(leftIcon, rightIcon);
    const hasIcon = !!(leftIcon || rightIcon);

    return (
      <div style={{ width: fullWidth ? '100%' : 'auto' }}>
        <InputContainer
          iconPosition={iconPosition}
          fullWidth={fullWidth}
          className={className}
        >
          {leftIcon}
          <StyledInput
            ref={ref}
            type={type}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            required={required}
            error={error}
            hasIcon={hasIcon}
            iconPosition={iconPosition}
            size={size}
            variant={variant}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            {...props}
          />
          {rightIcon}
        </InputContainer>
        {helperText && <HelperText error={error}>{helperText}</HelperText>}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
